import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Heading, Text, Button, Stack, HStack, Badge, Icon, Spinner, SimpleGrid, Input, IconButton } from "@chakra-ui/react";
import { Building2, UserCog, ImagePlus, Plus, Copy, Archive, RotateCcw, MoreHorizontal, Users, Search, ExternalLink, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { apiPost } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { ConfirmModal } from "@/components/ConfirmModal";
import { BulkCleanup } from "@/components/BulkCleanup";
import { SuperAdminInvite } from "@/components/SuperAdminInvite";
import { SegmentedTabs } from "@/components/SegmentedTabs";
import { CompanyAvatar, StatusPill, LOGO_TYPES, LOGO_MAX_MB } from "@/components/CompanyAvatar";
import { colors } from "@/theme/colors";

const Card = ({ children, testId }) => (
  <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="4" data-testid={testId}>{children}</Box>
);

const MenuItem = ({ onClick, color, children, testId }) => (
  <Flex as="button" type="button" onClick={onClick} data-testid={testId} role="menuitem"
    align="center" gap="2" w="full" px="3" py="2" borderRadius="8px" fontSize="sm" textAlign="left"
    color={color || colors.textPrimary} _hover={{ bg: colors.tealTint }}>
    {children}
  </Flex>
);

// Tidy summary card — summary only; all editing lives on the detail page.
function CompanyCard({ c, stats, logoUrl, onOpen, onToggleArchive, onPurge }) {
  const [menu, setMenu] = useState(false);
  const archived = c.active === false;

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menu]);

  return (
    <Box data-testid={`company-card-${c.id}`} onClick={onOpen} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
      bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" overflow="visible"
      cursor="pointer" position="relative" opacity={archived ? 0.72 : 1}
      transition="transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease"
      _hover={{ transform: "translateY(-3px)", boxShadow: "0 10px 24px rgba(31,41,55,0.12)", borderColor: "#BFE3E8" }}
      _focusVisible={{ outline: "none", boxShadow: "0 0 0 3px #BFE3E8" }}>
      <Box h="4px" bg={archived ? colors.border : colors.teal} borderTopRadius="13px" />
      <Box p="4">
        <Flex justify="space-between" align="flex-start" gap="2">
          <HStack gap="3" minW="0">
            <CompanyAvatar name={c.name} logoUrl={logoUrl} />
            <Box minW="0">
              <Text fontFamily="heading" fontWeight="600" color={colors.textPrimary} truncate data-testid={`company-name-${c.id}`}>{c.name}</Text>
              {c.industry && (
                <Text as="span" display="inline-block" mt="1" px="2" py="0.5" borderRadius="full" fontSize="xs"
                  bg={colors.cream} color={colors.textSecondary} data-testid={`industry-tag-${c.id}`}>{c.industry}</Text>
              )}
            </Box>
          </HStack>
          <Box position="relative" flexShrink={0} onClick={(e) => e.stopPropagation()}>
            <IconButton aria-label="Company actions" size="xs" variant="ghost" color={colors.textSecondary}
              _hover={{ bg: colors.tealTint, color: colors.tealHover }}
              onClick={() => setMenu((v) => !v)} data-testid={`company-card-menu-${c.id}`}>
              <MoreHorizontal size={16} />
            </IconButton>
            {menu && (
              <Stack position="absolute" right="0" top="calc(100% + 4px)" zIndex={20} bg={colors.white}
                border="1px solid" borderColor={colors.border} borderRadius="10px"
                boxShadow="0 8px 24px rgba(31,41,55,0.14)" p="1" gap="0" minW="160px" role="menu">
                <MenuItem onClick={onOpen} testId={`menu-open-${c.id}`}><ExternalLink size={13} /> Open</MenuItem>
                <MenuItem onClick={() => { setMenu(false); onToggleArchive(); }}
                  color={archived ? colors.tealHover : colors.poppy}
                  testId={archived ? `menu-restore-${c.id}` : `menu-archive-${c.id}`}>
                  {archived ? <><RotateCcw size={13} /> Restore</> : <><Archive size={13} /> Deactivate</>}
                </MenuItem>
                {archived && (
                  <MenuItem onClick={() => { setMenu(false); onPurge(); }} color={colors.poppy} testId={`menu-purge-${c.id}`}>
                    <Trash2 size={13} /> Delete
                  </MenuItem>
                )}
              </Stack>
            )}
          </Box>
        </Flex>
        <Flex mt="3.5" align="center" justify="space-between" gap="2" flexWrap="wrap">
          <StatusPill archived={archived} testId={`status-pill-${c.id}`} />
          <HStack gap="1.5" color={colors.textSecondary}>
            <Icon as={Users} boxSize="3.5" />
            <Text fontSize="xs" data-testid={`company-stats-${c.id}`}>
              {stats.users} {stats.users === 1 ? "user" : "users"} · {stats.on} of {stats.total} features
            </Text>
          </HStack>
        </Flex>
        {archived && (
          <Button size="xs" variant="outline" w="full" mt="3"
            onClick={(e) => { e.stopPropagation(); onToggleArchive(); }} data-testid={`restore-company-${c.id}`}>
            <RotateCcw size={13} /> Restore
          </Button>
        )}
      </Box>
    </Box>
  );
}

const createdMessage = (name, invitedEmail, emailed) => {
  if (!invitedEmail) return `Company "${name}" created.`;
  return emailed
    ? `Company "${name}" created — Org Admin invite email sent.`
    : `Company "${name}" created — share the invite link below.`;
};

// Super-Admin: create a brand-new client company (tenant) and optionally
// invite its first Org Admin. Company insert + first-admin invite happen
// server-side (super-admin-only endpoint).
function AddCompanyModal({ open, onClose, reload, flash, onInvite, onAttachPrompt }) {
  const empty = { name: "", industry: "", display_name: "", admin_email: "" };
  const [form, setForm] = useState(empty);
  const [logoFile, setLogoFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const close = () => { setForm(empty); setLogoFile(null); onClose(); };

  const pickLogo = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!LOGO_TYPES.test(f.type)) { flash("error", "Logo must be a PNG, JPG or WEBP image."); return; }
    if (f.size > LOGO_MAX_MB * 1024 * 1024) { flash("error", `Logo must be under ${LOGO_MAX_MB} MB.`); return; }
    setLogoFile(f);
  };

  const submit = async () => {
    if (!form.name.trim()) { flash("error", "Company name is required."); return; }
    setBusy(true);
    try {
      const res = await apiPost("/companies/create", {
        name: form.name.trim(),
        industry: form.industry.trim() || null,
        display_name: form.display_name.trim() || null,
        admin_email: form.admin_email.trim() || null,
        redirect_to: `${window.location.origin}/accept-invite`,
      });
      const company = res.company;
      if (logoFile && company?.id) {
        const ext = logoFile.type === "image/png" ? "png" : logoFile.type === "image/webp" ? "webp" : "jpg";
        const path = `${company.id}/logo-${Date.now()}.${ext}`;
        const { error: se } = await supabase.storage.from("company-logos").upload(path, logoFile, { contentType: logoFile.type });
        if (!se) await supabase.from("companies").update({ logo_path: path }).eq("id", company.id);
        else flash("error", `Company created, but logo upload failed: ${se.message}`);
      }
      const inv = res.invite;
      if (inv?.hashed_token) {
        onInvite({
          email: form.admin_email.trim(),
          link: `${window.location.origin}/accept-invite?token_hash=${inv.hashed_token}&type=recovery`,
          emailed: inv.emailed,
        });
      }
      if (res.invite_conflict) {
        if (res.invite_conflict.code === "exists_other") {
          onAttachPrompt({ company, email: form.admin_email.trim().toLowerCase() });
          flash("success", `Company "${company.name}" created.`);
        } else {
          flash("error", `Company "${company.name}" created, but the admin invite was blocked: ${res.invite_conflict.message}`);
        }
      } else {
        flash("success", createdMessage(company.name, form.admin_email.trim(), inv?.emailed));
      }
      close();
      reload();
    } catch (e) { flash("error", e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={close} title="Add company" testId="add-company-modal">
      <Stack gap="3">
        <Field label="Company name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Summit Builders Inc." bg={colors.white} borderColor={colors.border} data-testid="new-company-name" /></Field>
        <Field label="Industry"><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="e.g. Construction" bg={colors.white} borderColor={colors.border} data-testid="new-company-industry" /></Field>
        <Field label="Display name (shown beside Hakina brand)"><Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Defaults to company name" bg={colors.white} borderColor={colors.border} data-testid="new-company-display-name" /></Field>
        <Field label="Logo (optional)">
          <HStack gap="3">
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="new-company-logo-btn">
              <ImagePlus size={14} /> {logoFile ? "Change" : "Choose file"}
            </Button>
            <Text fontSize="xs" color={logoFile ? colors.textPrimary : colors.textSecondary}>{logoFile ? logoFile.name : `PNG/JPG/WEBP · max ${LOGO_MAX_MB} MB`}</Text>
            <input type="file" accept="image/png,image/jpeg,image/webp" ref={fileRef} onChange={pickLogo} data-testid="new-company-logo-input" style={{ position: "absolute", width: 1, height: 1, opacity: 0 }} />
          </HStack>
        </Field>
        <Field label="Invite first Org Admin (optional)"><Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} placeholder="admin@newcompany.com" bg={colors.white} borderColor={colors.border} data-testid="new-company-admin-email" /></Field>
        <Button onClick={submit} loading={busy} data-testid="create-company-submit">Create company</Button>
      </Stack>
    </Modal>
  );
}

const Field = ({ label, children }) => (
  <Box>
    <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>{label}</Text>
    {children}
  </Box>
);

// Type-to-confirm permanent deletion of a DEACTIVATED company (Super-Admin).
function PurgeCompanyModal({ company, users, onClose, reload, flash }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  if (!company) return null;
  const match = name.trim() === company.name;

  const submit = async () => {
    setBusy(true);
    try {
      await apiPost(`/platform/companies/${company.id}/purge`, { confirm_name: name.trim() });
      flash("success", `${company.name} and all of its data have been permanently deleted.`);
      onClose();
      reload();
    } catch (e) { flash("error", e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Delete company permanently?" testId="purge-company-modal">
      <Stack gap="3">
        <Text fontSize="sm" color={colors.textSecondary} data-testid="purge-warning-text">
          This permanently erases <Text as="span" fontWeight="600" color={colors.textPrimary}>{company.name}</Text> and
          ALL of its data — {pluralUsers(users)}, locations, workers, submissions, resources, contractors, audit history
          and uploaded files. This cannot be undone.
        </Text>
        <Field label="Type the company name to confirm">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={company.name}
            bg={colors.white} borderColor={colors.border} data-testid="purge-confirm-input" />
        </Field>
        <HStack justify="flex-end" gap="2">
          <Button size="sm" variant="outline" onClick={onClose} data-testid="purge-cancel-btn">Cancel</Button>
          <Button size="sm" bg={colors.poppy} color="white" _hover={{ opacity: 0.9 }}
            disabled={!match} loading={busy} onClick={submit} data-testid="purge-confirm-btn">
            <Trash2 size={14} /> Delete
          </Button>
        </HStack>
      </Stack>
    </Modal>
  );
}

const emptyCompaniesText = (seg, query) => {
  if (query) return `No ${seg === "active" ? "active" : "deactivated"} companies match “${query}”.`;
  return seg === "active" ? "No active companies yet." : "No deactivated companies — everyone is live.";
};

const pluralUsers = (n) => `${n} ${n === 1 ? "user" : "users"}`;

const archiveConfirmCopy = (company, users) => {
  if (!company) return { title: "", body: "", label: "", danger: false };
  if (company.active !== false) {
    return {
      title: `Deactivate ${company.name}?`,
      body: `This immediately locks out its ${pluralUsers(users)}. Their data is retained and you can restore access at any time.`,
      label: "Deactivate", danger: true,
    };
  }
  return {
    title: `Restore ${company.name}?`,
    body: `This re-enables access for its ${pluralUsers(users)} immediately.`,
    label: "Restore", danger: false,
  };
};

export default function Platform() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState({});
  const [logoUrls, setLogoUrls] = useState({});
  const [unassigned, setUnassigned] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newInvite, setNewInvite] = useState(null);
  const [seg, setSeg] = useState("active");
  const [query, setQuery] = useState("");
  const [confirm, setConfirm] = useState(null); // company pending deactivate/restore
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [purge, setPurge] = useState(null); // deactivated company pending permanent deletion
  const [attachPrompt, setAttachPrompt] = useState(null); // { company, email } — existing account to attach as Org Admin
  const [attachBusy, setAttachBusy] = useState(false);
  const flash = (type, text) => { setMsg({ type, text }); if (type !== "error") setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: comps }, { data: profs }, { data: mems }, { data: cat }, { data: feats }] = await Promise.all([
      supabase.from("companies").select("*").order("name"),
      supabase.from("profiles").select("id,full_name,email"),
      supabase.from("memberships").select("user_id,company_id"),
      supabase.from("feature_catalog").select("key,default_enabled"),
      supabase.from("company_features").select("company_id,feature_key,enabled"),
    ]);
    setCompanies(comps || []);
    const assigned = new Set((mems || []).map((m) => m.user_id));
    setUnassigned((profs || []).filter((p) => !assigned.has(p.id)));

    const total = (cat || []).length;
    const featMap = {};
    (comps || []).forEach((c) => { featMap[c.id] = Object.fromEntries((cat || []).map((f) => [f.key, f.default_enabled])); });
    (feats || []).forEach((r) => { if (featMap[r.company_id]) featMap[r.company_id][r.feature_key] = r.enabled; });
    const userCounts = {};
    (mems || []).forEach((m) => { userCounts[m.company_id] = (userCounts[m.company_id] || 0) + 1; });
    const s = {};
    (comps || []).forEach((c) => {
      s[c.id] = { users: userCounts[c.id] || 0, on: Object.values(featMap[c.id]).filter(Boolean).length, total };
    });
    setStats(s);

    const paths = (comps || []).filter((c) => c.logo_path).map((c) => c.logo_path);
    if (paths.length) {
      const { data: signed } = await supabase.storage.from("company-logos").createSignedUrls(paths, 3600);
      const byPath = Object.fromEntries((signed || []).filter((r) => r.signedUrl).map((r) => [r.path, r.signedUrl]));
      setLogoUrls(Object.fromEntries((comps || []).filter((c) => c.logo_path).map((c) => [c.id, byPath[c.logo_path] || null])));
    } else setLogoUrls({});
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeCompanies = companies.filter((c) => c.active !== false);
  const deactivated = companies.filter((c) => c.active === false);
  const q = query.trim().toLowerCase();
  const shown = (seg === "active" ? activeCompanies : deactivated)
    .filter((c) => !q || c.name.toLowerCase().includes(q));

  const doToggleArchive = async () => {
    const c = confirm;
    const archiving = c.active !== false;
    setConfirmBusy(true);
    const { error } = await supabase.from("companies")
      .update({ active: !archiving, archived_at: archiving ? new Date().toISOString() : null }).eq("id", c.id);
    setConfirmBusy(false);
    setConfirm(null);
    if (error) { flash("error", error.message); return; }
    flash("success", archiving ? `${c.name} deactivated — its users are now locked out.` : `${c.name} restored — access re-enabled.`);
    load();
  };

  const confirmCopy = archiveConfirmCopy(confirm, confirm ? (stats[confirm.id]?.users ?? 0) : 0);

  // Super-Admin confirmed: attach the first-admin membership to the EXISTING login.
  const attachExistingAdmin = async () => {
    const { company, email } = attachPrompt;
    setAttachBusy(true);
    try {
      await apiPost(`/platform/companies/${company.id}/users/invite`, {
        email, role: "org_admin", attach_existing: true,
        redirect_to: `${window.location.origin}/accept-invite`,
      });
      flash("success", `${email} added to ${company.name} as Org Admin — they keep their existing login and password.`);
      setAttachPrompt(null);
      load();
    } catch (e) { flash("error", e.message); } finally { setAttachBusy(false); }
  };

  const assign = async (userId) => {
    const companyId = sel[userId] || activeCompanies[0]?.id;
    if (!companyId) return;
    setBusy(userId);
    try {
      const { error } = await supabase.from("memberships").insert({ user_id: userId, company_id: companyId, role: "field_employee", status: "pending" });
      if (error) throw error;
      const cName = companies.find((c) => c.id === companyId)?.name;
      flash("success", `Assigned to ${cName} (pending). Their Org Admin can now set a role.`);
      load();
    } catch (e) { flash("error", e.message); } finally { setBusy(null); }
  };

  return (
    <Box data-testid="platform-page">
      <Heading fontFamily="heading" fontWeight="700" fontSize={{ base: "2xl", md: "3xl" }} color={colors.textPrimary}>Companies & Users</Heading>
      <Text color={colors.textSecondary} mb="5">Platform Super-Admin — manage tenants and onboard new sign-ups.</Text>

      {msg && (
        <Box mb="4" px="4" py="2.5" borderRadius="10px" fontSize="sm" bg={msg.type === "error" ? colors.criticalBg : colors.resolvedBg} color={msg.type === "error" ? colors.criticalText : colors.resolvedText} data-testid="platform-banner">{msg.text}</Box>
      )}

      {loading ? (
        <Flex minH="30vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>
      ) : (
        <Stack gap="8">
          <Box>
            <Flex justify="space-between" align="center" gap="3" mb="3" flexWrap="wrap">
              <HStack gap="2"><Icon as={Building2} color={colors.teal} boxSize="5" /><Heading fontFamily="heading" fontSize="lg">Companies</Heading></HStack>
              <Button onClick={() => setAddOpen(true)} data-testid="add-company-btn">
                <Plus size={16} /> Add Company
              </Button>
            </Flex>

            <Flex gap="3" mb="4" align="center" flexWrap="wrap">
              <SegmentedTabs value={seg} onChange={setSeg} tabs={[
                { key: "active", label: `Active ${activeCompanies.length}`, testId: "seg-active" },
                { key: "deactivated", label: `Deactivated ${deactivated.length}`, testId: "seg-deactivated" },
              ]} />
              <Flex align="center" position="relative" flex="1" minW="220px" maxW="360px">
                <Icon as={Search} boxSize="4" color={colors.textSecondary} position="absolute" left="3" pointerEvents="none" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search companies…"
                  pl="9" bg={colors.white} borderColor={colors.border} borderRadius="11px" data-testid="platform-search" />
              </Flex>
            </Flex>

            {newInvite && (
              <Box mb="3" p="4" borderRadius="12px" border="1px solid" borderColor={colors.teal} bg={colors.tealTint} data-testid="new-company-invite">
                <Flex justify="space-between" align="flex-start" gap="2">
                  <Text fontSize="sm" fontWeight="600" color={colors.textPrimary} mb="1">Org Admin invite for {newInvite.email}{newInvite.emailed ? " (email sent)" : ""}</Text>
                  <Button size="xs" variant="ghost" onClick={() => setNewInvite(null)} data-testid="dismiss-invite">Dismiss</Button>
                </Flex>
                <Text fontSize="xs" color={colors.textSecondary} mb="2">
                  {newInvite.emailed
                    ? "Invite email sent — this link always works as a backup."
                    : "Share this link with the new Org Admin so they can set a password and log in."}
                </Text>
                <HStack gap="2">
                  <Input size="sm" readOnly value={newInvite.link} bg={colors.white} borderColor={colors.border} fontSize="xs" data-testid="new-company-invite-link" />
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(newInvite.link); flash("success", "Invite link copied."); }} data-testid="copy-invite-link">
                    <Copy size={14} /> Copy
                  </Button>
                </HStack>
              </Box>
            )}

            {shown.length === 0 ? (
              <Card testId="companies-empty">
                <Text color={colors.textSecondary} fontSize="sm">{emptyCompaniesText(seg, query.trim())}</Text>
              </Card>
            ) : (
              <SimpleGrid minChildWidth="290px" gap="3.5">
                {shown.map((c) => (
                  <CompanyCard key={c.id} c={c} stats={stats[c.id] || { users: 0, on: 0, total: 4 }}
                    logoUrl={logoUrls[c.id] || null}
                    onOpen={() => navigate(`/platform/company/${c.id}`)}
                    onToggleArchive={() => setConfirm(c)}
                    onPurge={() => setPurge(c)} />
                ))}
              </SimpleGrid>
            )}
          </Box>

          <Box>
            <HStack gap="2" mb="3"><Icon as={UserCog} color={colors.teal} boxSize="5" /><Heading fontFamily="heading" fontSize="lg">Unassigned sign-ups</Heading><Badge bg={colors.poppyTint} color={colors.poppy} borderRadius="full" px="2.5">{unassigned.length}</Badge></HStack>
            {unassigned.length === 0 ? (
              <Card testId="unassigned-empty"><Text color={colors.textSecondary} fontSize="sm">No unassigned users. New self-sign-ups will appear here.</Text></Card>
            ) : (
              <Stack gap="3">
                {unassigned.map((u) => (
                  <Card key={u.id} testId={`unassigned-row-${u.id}`}>
                    <Flex justify="space-between" align="center" gap="3" flexWrap="wrap">
                      <Box>
                        <Text fontWeight="600" color={colors.textPrimary}>{u.full_name || "(no name)"}</Text>
                        <Text fontSize="sm" color={colors.textSecondary}>{u.email}</Text>
                      </Box>
                      <HStack gap="2">
                        <Box as="select" value={sel[u.id] || activeCompanies[0]?.id || ""} onChange={(e) => setSel({ ...sel, [u.id]: e.target.value })} data-testid={`company-select-${u.id}`} px="3" py="2" borderRadius="8px" border="1px solid" borderColor={colors.border} bg={colors.white} fontSize="sm">
                          {activeCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </Box>
                        <Button size="sm" onClick={() => assign(u.id)} loading={busy === u.id} data-testid={`assign-${u.id}`}>Assign to company</Button>
                      </HStack>
                    </Flex>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>

          <SuperAdminInvite flash={flash} />

          <BulkCleanup flash={flash} reload={load} />
        </Stack>
      )}

      <ConfirmModal open={!!confirm} onClose={() => setConfirm(null)}
        title={confirmCopy.title} body={confirmCopy.body}
        confirmLabel={confirmCopy.label} danger={confirmCopy.danger}
        busy={confirmBusy} onConfirm={doToggleArchive} />

      <AddCompanyModal open={addOpen} onClose={() => setAddOpen(false)} reload={load} flash={flash}        onInvite={setNewInvite} onAttachPrompt={setAttachPrompt} />

      <ConfirmModal open={!!attachPrompt} onClose={() => setAttachPrompt(null)}
        title="Add existing account?"
        body={attachPrompt ? `This email already belongs to an existing Hakina account. Add them to ${attachPrompt.company.name} as a new Org Admin membership? They keep their one existing login and password — no password-set email is sent.` : ""}
        confirmLabel="Add membership"
        busy={attachBusy} onConfirm={attachExistingAdmin} testId="attach-existing-admin-modal" />

      {purge && (
        <PurgeCompanyModal company={purge} users={stats[purge.id]?.users ?? 0}
          onClose={() => setPurge(null)} reload={load} flash={flash} />
      )}
    </Box>
  );
}
